import React, { useCallback, useMemo, useState } from 'react';
import type { DataTableRenderCellParams, DataTableRowEventHandler } from '@/components/custom/erp/data-table-v2';
import { ContextMenuBuilder, ContextMenu } from '@/components/custom/erp/input-table-v2';
import { SelectedCellInfo } from '../hooks/useAdvSettlementRows';
import { ErpInputTableV2 } from '@/components/custom/erp';
import { getAdvSettlementTableColumns } from './columns';
import { copyCellValue } from '@/lib/table-handlers';
import { useAdvSettlementPaste } from '../hooks';
import { FormMode } from '@/types/form';

interface AdvSettlementTabProps {
  formMode: FormMode;
  rows: { uuid?: string | null }[];
  selectedRowUuid?: string | null;
  selectedCell?: SelectedCellInfo | null;
  onRowClick?: DataTableRowEventHandler<MuaHangRecord>;
  onCellClick?: (params: DataTableRenderCellParams<MuaHangRecord>) => void;
  onAddRow: () => void;
  onDeleteRow: () => void;
  onCopyRow: () => void;
  onPasteRow: (clipboardText?: string) => void | Promise<void>;
  onMoveRow: (direction: 'up' | 'down') => void;
  onCellValueChange: (rowUuid: string, field: string, newValue: MuaHangRecord) => void;
  handleUpdateNonWritableFields?: (fields: Set<string>) => void;
  handleUpdatePasteConfigs?: (
    configs: Record<string, { resolver: (codes: string[]) => Promise<MuaHangRecord[]>; resultField?: string }>
  ) => void;
  isPasting?: boolean;
}

const ADV_ACTION_ORDER = ['add', 'delete', 'copy', 'paste', 'moveUp', 'moveDown', 'export', 'pin'] as const;
const noop = () => {};

export const AdvSettlementTab: React.FC<AdvSettlementTabProps> = ({
  formMode,
  rows,
  selectedRowUuid,
  selectedCell,
  onRowClick,
  onCellClick,
  onAddRow,
  onDeleteRow,
  onCopyRow,
  onPasteRow,
  onMoveRow,
  onCellValueChange,
  handleUpdateNonWritableFields,
  handleUpdatePasteConfigs,
  isPasting
}) => {
  const [contextMenu, setContextMenu] = useState<{
    position: { x: number; y: number } | null;
    rowData: MuaHangRecord | null;
    columnField: string | null;
  }>({
    position: null,
    rowData: null,
    columnField: null
  });
  const columns = useMemo(
    () => getAdvSettlementTableColumns(formMode, onCellValueChange),
    [formMode, onCellValueChange]
  );

  useAdvSettlementPaste({
    columns,
    handleUpdateNonWritableFields,
    handleUpdatePasteConfigs
  });

  const contextMenuOptions = useMemo(
    () =>
      ContextMenuBuilder.buildOptions({
        contextMenu,
        columns,
        getRowId: row => row?.uuid,
        onCopyData: ({ columnValue, columnField, columnValues }) => {
          copyCellValue({
            columnField,
            columnValue,
            columnValues
          });
        },
        rowsCount: rows.length
      }),
    [columns, contextMenu, rows.length]
  );
  const handleRowId = useCallback((row: MuaHangRecord) => row?.uuid || '', []);
  const handleCellContextMenu = useCallback(
    (event: MouseEvent | React.MouseEvent, params: DataTableRenderCellParams) => {
      onCellClick?.(params);
      setContextMenu({
        position: { x: event.clientX, y: event.clientY },
        rowData: params.row,
        columnField: params.field
      });
    },
    [onCellClick]
  );
  const tableActions = useMemo(
    () => ({
      add: onAddRow,
      delete: onDeleteRow,
      copy: onCopyRow,
      paste: onPasteRow,
      moveUp: () => onMoveRow('up'),
      moveDown: () => onMoveRow('down'),
      export: noop,
      pin: noop
    }),
    [onAddRow, onCopyRow, onDeleteRow, onMoveRow, onPasteRow]
  );

  return (
    <>
      <ErpInputTableV2
        rows={rows}
        onRowClick={onRowClick as MuaHangRecord}
        onCellClick={onCellClick as MuaHangRecord}
        onCellContextMenu={handleCellContextMenu as MuaHangRecord}
        selectedRowId={selectedRowUuid || undefined}
        columns={columns}
        getRowId={handleRowId}
        pasteTarget={selectedCell}
        actionLoading={isPasting}
        actionsDisabled={formMode === 'view'}
        actionOrder={ADV_ACTION_ORDER as MuaHangRecord}
        actions={tableActions}
      />
      <ContextMenu
        options={contextMenuOptions}
        position={contextMenu.position}
        onClose={() => {
          setContextMenu({
            position: null,
            rowData: null,
            columnField: null
          });
        }}
      />
    </>
  );
};

export default AdvSettlementTab;
